import { Cell, Icon, Row } from '@dexma/ui-components';
import { ConfigFieldType, GaugeRangeValue } from 'adapter/in/common/form/types';
import ColorPickerInputContainer from 'adapter/in/common/form/ColorPicker';
import TextInputContainer from 'adapter/in/common/form/Text';
import { OPTIONS } from 'adapter/in/common/widget/options';
import {
    StyledCellRangeGroupContainer,
    StyledRemoveRangeButton,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range/style';
import { RemoveElementRHF } from 'adapter/in/common/hooks/useArrayField';
import { validationGreaterThan, validationIntersectionRanges, validationIsNumber } from 'adapter/in/common/utils/validation';
import i18n from 'asset/translation';
import React, { memo } from 'react';

type RangeGroupContainerProps = {
    item: GaugeRangeValue & { id: string };
    count: number;
    index: number;
    getValue: (fieldName: string) => GaugeRangeValue;
    handleRemoveRange: (index?: RemoveElementRHF) => void;
};

const RangeGroupContainer = memo((props: RangeGroupContainerProps) => {
    const { count, index, item, getValue, handleRemoveRange } = props;
    return (
        <StyledCellRangeGroupContainer key={item.id}>
            <StyledRemoveRangeButton>
                <Cell onClick={() => handleRemoveRange(index)}>
                    <Icon name='add' ariaLabel={i18n.t('widgets.widgetMenu.ranges.removeRangeAriaLabel')} />
                </Cell>
            </StyledRemoveRangeButton>
            <Row>
                <Cell>
                    <TextInputContainer
                        configType={ConfigFieldType.OPTIONS}
                        name={`${OPTIONS.RANGES}.${index}.name`}
                        label={i18n.t('widgets.widgetMenu.ranges.name')}
                        properties={{ maxLength: 100, placeholder: '' }}
                        footer=''
                        defaultValue={item.name}
                    />
                </Cell>
                <Cell style={{ paddingLeft: '0.7rem' }}>
                    <ColorPickerInputContainer
                        showInput
                        configType={ConfigFieldType.OPTIONS}
                        name={`${OPTIONS.RANGES}.${index}.color`}
                        label={i18n.t('widgets.widgetMenu.ranges.color')}
                        defaultValue={item.color}
                    />
                </Cell>
            </Row>
            <Row>
                <Cell>
                    <TextInputContainer
                        configType={ConfigFieldType.OPTIONS}
                        name={`${OPTIONS.RANGES}.${index}.from`}
                        label={i18n.t('widgets.widgetMenu.ranges.from')}
                        properties={{ maxLength: 100, placeholder: '' }}
                        footer=''
                        defaultValue={item.from}
                        rules={{
                            required: true,
                            validate: {
                                isNumber: validationIsNumber,
                                intersection: validationIntersectionRanges(
                                    [...Array(count)].map((_, i) => ({
                                        key: i,
                                        from: getValue(`${OPTIONS.RANGES}.${i}`).from,
                                        to: getValue(`${OPTIONS.RANGES}.${i}`).to,
                                    })),
                                    index
                                ),
                            },
                        }}
                    />
                </Cell>
                <Cell>
                    <TextInputContainer
                        configType={ConfigFieldType.OPTIONS}
                        name={`${OPTIONS.RANGES}.${index}.to`}
                        label={i18n.t('widgets.widgetMenu.ranges.to')}
                        properties={{ maxLength: 100, placeholder: '' }}
                        footer=''
                        defaultValue={item.to}
                        rules={{
                            required: true,
                            validate: {
                                isNumber: validationIsNumber,
                                greaterThanTo: validationGreaterThan(
                                    Number(getValue(`${OPTIONS.RANGES}.${index}`)?.from.value),
                                    'widgets.widgetMenu.ranges.from'
                                ),
                                intersection: validationIntersectionRanges(
                                    [...Array(count)].map((_, i) => ({
                                        key: i,
                                        from: getValue(`${OPTIONS.RANGES}.${i}`).from,
                                        to: getValue(`${OPTIONS.RANGES}.${i}`).to,
                                    })),
                                    index
                                ),
                            },
                        }}
                    />
                </Cell>
            </Row>
        </StyledCellRangeGroupContainer>
    );
});

export default RangeGroupContainer;
